import {
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ArcElement,
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  DoughnutController,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from 'chart.js';
import {
  BaseComponent,
  LoadingState,
  PolicyCalc,
  SearchResult,
  getUrlParams,
} from 'src/app/shared';
import { LeaveBalances } from 'src/app/shared/models/LeaveBalanceInformation';
import { MyClaim } from '../../../shared';
import { ClaimService } from '../../services/claim.service';
import { UserRoleService } from '../../services/user-role.service';
import { HeaderLevel } from '../../../shared/components/controls/panel.component';
import { Session } from '../../services/session.service';
import { LeaveBalanceInformation } from '../../../shared/models/LeaveBalanceInformation';
import { TranslationRequest } from 'src/app/shared/models/translation/translation-request';
import { TranslationPipe } from '../../pipes/translation.pipe';

@Component({
    selector: 'app-leave-balance',
    template: `
    <app-section class="tw-w-full" [IsSmallBox]="false">
      @if (showClaimBreadcrum) {
        <div class="app-pl2 tw-w-full">
          <app-claim-breadcrumb></app-claim-breadcrumb>
        </div>
      }
      @if (!hidePanel) {
        <app-panel
          [title]="titlePanel"
          [headerLevel]="headerLevel"
          class="tw-w-full"
          [expanded]="panelExpanded"
          [canExpand]="canExpand"
          >
          <app-loading-panel
            [skeletonLines]="3"
            [state]="loading"
            [error]="error"
            >
            <ng-template #content>
              @if (showBalance) {
                <div class="tw-flex tw-flex-wrap tw-w-full">
                  <div class="app-full-width">
                    <fieldset>
                      <legend
                        id="balance-summary-group"
                        class="tw-italic"
                        [innerHTML]="help | translation: translationRequest"
                      ></legend>
                      <legend id="balance-summary-group" class="tw-hidden">
                        {{'View leave balance summary by'}}:
                      </legend>
                      @if (leaveBalanceInformation.changeableDuration) {
                        <mat-radio-group
                          [(ngModel)]="duration"
                          style="float:right;"
                          (change)="onSelectedValueChange($event.value)"
                          aria-labelledby="balance-summary-group"
                          role="radiogroup"
                          aria-live="polite"
                          >
                          <mat-radio-button
                            class=""
                            value="hours"
                            tabindex="0"
                            aria-label="Selected Hours"
                            >
                            {{'Hours' | translation : translationRequest}}
                          </mat-radio-button>
                          <mat-radio-button
                            class="app-pl2"
                            value="days"
                            tabindex="0"
                            aria-label="Selected Days"
                            >
                            {{'Days' | translation : translationRequest}}
                          </mat-radio-button>
                          <mat-radio-button
                            checked="true"
                            class="app-pl2"
                            value="weeks"
                            tabindex="0"
                            aria-label="Selected Weeks"
                            >
                            {{'Weeks' | translation : translationRequest}}
                          </mat-radio-button>
                        </mat-radio-group>
                      }
                    </fieldset>
                  </div>
                  <div class="tw-w-full tw-flex tw-flex-col tw-pb-4 overflow-auto">
                    @for (item of chartItems; track item) {
                      <div
                        class="tw-w-full app-blue-container tw-py-2 tw-mb-3 tw-flex tw-flex-wrap tw-items-center"
                        >
                        <div class="chart-title tw-w-full lg:tw-w-[35%] tw-py-2">
                          {{ item.policyCalc.name.trim() | translation: translationRequest }} ({{ item.policyCalc.maxWeeks }}
                          {{ item.policyCalc.calculationMethod | translation: translationRequest }})
                        </div>
                        <div class="chart-canvas-div tw-w-full lg:tw-w-[65%] tw-pr-2">
                          <canvas
                            [attr.role]="'img'"
                            [attr.id]="item.id"
                      [attr.aria-label]="
                        'Chart for ' +
                        item.policyCalc.name +
                        ' ' +
                        item.policyCalc.availableWeeks +
                        ' available ' +
                        duration +
                        ', ' +
                        item.policyCalc.pendingWeeks +
                        ' pending ' +
                        duration +
                        ', ' +
                        item.policyCalc.usedWeeks +
                        ' used ' +
                        duration
                      "
                            class="chart-canvas"
                            >{{ item.chart }}</canvas
                            >
                          </div>
                        </div>
                      }
                    </div>
                    <div class="tw-w-full">
                      @for (policy of mismatchedPolicies; track policy) {
                        <div
                          class="policy-item"
                          >
                          <p class="chart-title">
                            {{ policy.leavePolicy.trim() | translation: translationRequest }} : {{ unitMismatchMessage | translation : translationRequest }}
                          </p>
                        </div>
                      }
                    </div>
                    <app-action-row class="tw-w-full">
                      @if (showClaimBreadcrum) {
                        <app-button
                          right2
                          emphasis="High"
                          (click)="onClose()"
                          label="Close"
                          >
                        </app-button>
                      }
                    </app-action-row>
                  </div>
                }
                @if (!showBalance) {
                  <div
                    class="tw-flex tw-flex-col tw-items-center"
                    >
                    <div>
                      <mat-icon class="tw-text-[var(--sed-black-80-color)]"
                        >list_alt</mat-icon
                        >
                      </div>
                      <div
                        class="tagline-header app-pt1 app-pb1 tw-text-[var(--sed-black-80-color)]"
                        >
                        <strong>{{ noDataMessage}}</strong>
                      </div>
                    </div>
                  }
                  <div aria-atomic="true" aria-relevant="all" class="sr-only">
                    <span>{{ liveRegionContent | translation : translationRequest }}</span>
                  </div>
                </ng-template>
              </app-loading-panel>
            </app-panel>
          }
        </app-section>
    `,
    styles: [
        `
      .pb15 {
        padding-bottom: 15px;
      }
      .chart-title {
        font-size: 16px;
        font-weight: bold;
        padding: 8px 0px 8px 16px;
        white-space: normal;
        overflow-wrap: break-word;
      }
      .message {
        font-weight: bold;
      }
      .chart-canvas {
        max-width: 320px;
        max-height: 6.39rem;
        min-width: 140px;
        min-height: 6.38rem;
      }
      .chart-canvas-div {
        text-align: center;
      }
      .icon {
        font-size: 64px;
        width: 64px;
        height: 64px;
      }

      .view-link {
        font-size: 20px;
      }
    `,
    ],
    standalone: false
})
export class LeaveBalanceComponent
  extends BaseComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  duration: string = 'weeks';
  titlePanel: string = 'Leave Balance Summary';
  chart: any;

  chartItems: ChartItem[] = [];

  @Input()
  translationRequest: TranslationRequest;
  

  @Input()
  showBalance = true;

  @Input()
  hidePanel = false;

  @Input()
  claims: MyClaim[];

  @Input()
  policyCalcs: PolicyCalc[] = [];

  @Input()
  canExpand: boolean = false;

  @Input()
  headerLevel: HeaderLevel = 1;

  leaveBalance: LeaveBalances[] = [];
  leaveBalanceInformation: LeaveBalanceInformation;

  showClaimBreadcrum: boolean = false;
  claimId: string;
  panelExpanded: boolean = true;
  source: string;
  noDataMessage: string;
  hasOpenLeaveClaims: boolean = false;
  help: string =
    'Available hours and days are an estimate and can vary based on changes to the work schedule. They are meant for informational purposes based on the information available at the time. Actual leave balances are calculated in weeks and portion of weeks rather than hours or days per policy guidelines. <br><br> Pending represents future time away from work or time away from work where a decision has not yet been made.';

  error: any;
  loading: LoadingState = 'Loading';
  claim: SearchResult;
  liveRegionContent: string = '';
  leaveType: string;
  mismatchedPolicies: LeaveBalances[] = [];
  unitMismatchMessage: string =
    'Entitlements under this policy cannot be calculated at this time.';

  // Translated label variables
  usedWeeksLabel: string = '';
  pendingWeeksLabel: string = '';
  availableWeeksLabel: string = '';
  usedDaysLabel: string = '';
  pendingDaysLabel: string = '';
  availableDaysLabel: string = '';
  usedHoursLabel: string = '';
  pendingHoursLabel: string = '';
  availableHoursLabel: string = '';

  constructor(
    private router: Router,
    private claimService: ClaimService,
    private route: ActivatedRoute,
    private userRole: UserRoleService,
    public session: Session,
    public trnspipe : TranslationPipe
  ) {
    super();
  }
  private async loadClaimFromUrl() {
    const params = getUrlParams();
    const fromClaim = params.fromClaim;
    this.showClaimBreadcrum = fromClaim && fromClaim == 'yes' ? true : false;
    this.claimId = params.claimId;
    this.source = params.source;
  }

  loadBalance() {
    if (this.leaveBalance && this.leaveBalance.length > 0) {
      this.showBalance = true;
      sessionStorage.setItem('showBalance', 'true');
      setTimeout(() => {
        for (const item of this.chartItems) {
          const labels = this.calculateChartLabels(item.policyCalc);
          this.buildChartBar(item, labels);
        }
      }, 0);
    }
  }

  calculateChartLabels(policyCalc: PolicyCalc): string[] {
    const calculationMethod = policyCalc.calculationMethod.toLowerCase();
    let usedLabel: string;
    let pendingLabel: string;
    let availableLabel: string;
    
    if (calculationMethod === 'weeks') {
      usedLabel = `${policyCalc.usedWeeks} ${this.usedWeeksLabel}`;
      pendingLabel = `${policyCalc.pendingWeeks} ${this.pendingWeeksLabel}`;
      availableLabel = `${policyCalc.availableWeeks} ${this.availableWeeksLabel}`;
    } else if (calculationMethod === 'days') {
      usedLabel = `${policyCalc.usedWeeks} ${this.usedDaysLabel}`;
      pendingLabel = `${policyCalc.pendingWeeks} ${this.pendingDaysLabel}`;
      availableLabel = `${policyCalc.availableWeeks} ${this.availableDaysLabel}`;
    } else if (calculationMethod === 'hours') {
      usedLabel = `${policyCalc.usedWeeks} ${this.usedHoursLabel}`;
      pendingLabel = `${policyCalc.pendingWeeks} ${this.pendingHoursLabel}`;
      availableLabel = `${policyCalc.availableWeeks} ${this.availableHoursLabel}`;
    }

    return [usedLabel, pendingLabel, availableLabel];
  }

  buildChartBar(chart: ChartItem, labels: string[]) {
    const policyCalc = chart.policyCalc;
    const id = chart.id;
    if (!document.getElementById(id) || chart.chart) {
      return;
    }
    chart.chart = new Chart(id, {
      type: 'doughnut',
      data: {
        labels: labels,
        datasets: [
          {
            label: '',
            data: [
              policyCalc.usedWeeks,
              policyCalc.pendingWeeks,
              policyCalc.availableWeeks,
            ],
            backgroundColor: ['#ba49ad', '#b6c4db', '#3c8bf7'],
            borderColor: '#000000',
            borderWidth: 1,
            borderAlign: 'center',
          },
        ],
      },
      options: {
        animation: false,
        responsive: true,
        elements: {
          arc: {
            borderWidth: 0,
          },
        },
        layout: {
          padding: {
            left: -80,
          },
        },
        plugins: {
          legend: {
            position: 'right',
            labels: {
              font: {
                size: 13,
                weight: 'bold',
                family: '"Source Sans Pro", "Helvetica Neue", sans-serif',
              },
            },
            onClick: (event: any) => {
              event.native?.stopPropagation();
            },
          },
          title: {
            display: false,
            text: policyCalc.name,
          },
          tooltip: {
            enabled: false,
          },
        },
      },
    });
    chart.chart.update('none');
  }
  ngAfterViewInit(): void {
    if (this.showBalance) {
      setTimeout(() => {
        this.loadBalance();
      }, 0);
    }
  }

  async ngOnInit() {
    // Initialize translated labels
    this.usedWeeksLabel = this.trnspipe.transform('Used Weeks', this.translationRequest);
    this.pendingWeeksLabel = this.trnspipe.transform('Pending Weeks', this.translationRequest);
    this.availableWeeksLabel = this.trnspipe.transform('Available Weeks', this.translationRequest);
    this.usedDaysLabel = this.trnspipe.transform('Used Days', this.translationRequest);
    this.pendingDaysLabel = this.trnspipe.transform('Pending Days', this.translationRequest);
    this.availableDaysLabel = this.trnspipe.transform('Available Days', this.translationRequest);
    this.usedHoursLabel = this.trnspipe.transform('Used Hours', this.translationRequest);
    this.pendingHoursLabel = this.trnspipe.transform('Pending Hours', this.translationRequest);
    this.availableHoursLabel = this.trnspipe.transform('Available Hours', this.translationRequest);
    
    Chart.register(
      DoughnutController,
      BarController,
      CategoryScale,
      BarElement,
      LinearScale,
      ArcElement,
      Legend,
      Title,
      Tooltip
    );
    this.leaveType = this.session.user.leaveType;
    this.subs.sink = this.route.queryParams.subscribe((p) =>
      this.loadClaimFromUrl()
    );

    try {
      if (this.leaveType !== 'LeaveLink') {
        await this.GetLeaveBalanceSummary();
      } else {
        await this.GetLeaveLinkLeaveBalanceSummary();
      }

      if (this.claims) {
        this.hasOpenLeaveClaims = this.claims.some(
          (claim) =>
            claim.lineOfBusiness === 'LV' &&
            claim.status.toUpperCase() === 'OPEN'
        );
      }
      this.setPanelStatus();
      if (this.leaveBalance && this.leaveBalance.length > 0) {
        this.UpdateChart();
      } else {
        this.showBalance = false;
        this.noDataMessage = this.trnspipe.transform('You do not have any leave balance information',this.translationRequest);
        if (!this.hasOpenLeaveClaims) {
          this.noDataMessage = this.trnspipe.transform('You do not have any open leave claims.',this.translationRequest);
        }
        sessionStorage.setItem('showBalance', 'false');
      }
      this.loading = 'Loaded';
    } catch (error) {
      this.error = error;
    }
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    for (const item of this.chartItems) {
      item.chart?.destroy();
    }
  }
  onClose() {
    this.router.navigate(['/']);
  }
  async onSelectedValueChange(event: string) {
    if (event === 'weeks') {
      this.duration = 'weeks';
    } else if (event === 'hours') {
      this.duration = 'hours';
    } else if (event === 'days') {
      this.duration = 'days';
    }
    if (!this.leaveBalance || this.leaveBalance.length == 0) {
      await this.GetLeaveBalanceSummary();
    }

    setTimeout(() => {
      this.UpdateChart();
    }, 500);

    this.liveRegionContent = '';
    for (let i = 0; i < this.chartItems.length; i++) {
      const currentItemAnnouncement = this.chartItems[i].policyCalc
        ? `Duration changed to ${this.duration}. ${this.chartItems[i].policyCalc.name} has ${this.chartItems[i].policyCalc.availableWeeks} available ${this.duration}, a maximum of ${this.chartItems[i].policyCalc.maxWeeks} ${this.duration}, and currently has ${this.chartItems[i].policyCalc.pendingWeeks}  ${this.duration} pending, and currently has ${this.chartItems[i].policyCalc.usedWeeks} ${this.duration} used.`
        : null;
      this.liveRegionContent += currentItemAnnouncement + '\n';
    }
  }

  async GetLeaveBalanceSummary() {
    let result;
    if (
      this.userRole.manager &&
      this.claims &&
      this.claims.length > 0 &&
      this.claims.some((x) => x.lineOfBusiness === 'LV')
    ) {
      if (this.claims) {
        this.hasOpenLeaveClaims = this.claims.some(
          (claim) =>
            claim.lineOfBusiness === 'LV' &&
            claim.status.toUpperCase() === 'OPEN'
        );
      }
      result = await this.claimService.getLeaveBalanceSummary(
        this.claims[0].empUnum,
        this.claims[0].hrMasterUid
      );
    } else if (this.userRole.employee) {
      const claims = await this.claimService.getClaims();
      if (claims) {
        this.hasOpenLeaveClaims = claims.some(
          (claim) =>
            claim.lineOfBusiness === 'LV' &&
            claim.status.toUpperCase() === 'OPEN'
        );
      }
      if (claims.some((x) => x.lineOfBusiness === 'LV')) {
        const lvClaim = claims.find((x) => x.lineOfBusiness === 'LV');
        result = await this.claimService.getLeaveBalanceSummary(
          lvClaim.empUnum,
          lvClaim.hrMasterUid
        );
      }
    } else {
      this.claim = await this.claimService.getSummary(
        this.source,
        this.claimId
      );
      if (
        this.claim &&
        this.claim.lob === 'LV' &&
        this.claim.statusCode === 'O'
      ) {
        this.hasOpenLeaveClaims = true;
      } else {
        const claims = await this.claimService.getClaimsEmpUNum(
          this.claim.empUnum
        );
        if (claims) {
          this.hasOpenLeaveClaims = claims.some(
            (claim) =>
              claim.lineOfBusiness === 'LV' &&
              claim.status.toUpperCase() === 'OPEN'
          );
        }
      }
      result = await this.claimService.getLeaveBalanceSummary(
        this.claim.empUnum,
        this.claim.hrMasterUid
      );
    }
    if (
      result &&
      result?.leaveBalances &&
      result?.leaveBalances.length > 0 &&
      this.hasOpenLeaveClaims
    ) {
      this.leaveBalanceInformation = result;
      this.leaveBalance = result.leaveBalances;
    }
  }

  async GetLeaveLinkLeaveBalanceSummary() {
    let result;
    let empId;
    let empUnum;
    if (this.claims) {
      empId = this.claims[0].empId;
      empUnum = this.claims[0].empUnum;
    } else {
      this.claim = await this.claimService.getSummary(
        this.source,
        this.claimId
      );

      empId = this.claim.employeeId;
      empUnum = this.claim.empUnum;
    }
    result = await this.claimService.getLeaveLinkBalanceSummary(empUnum, empId);
    this.leaveBalanceInformation = result;
    this.leaveBalance = result.leaveBalances;
  }

  UpdateChart() {
    this.policyCalcs = [];
    this.leaveBalance.forEach((element) => {
      if (!this.leaveBalanceInformation.changeableDuration) {
        this.duration = element.unit;
      }
      if (this.duration === 'weeks') {
        const week = {
          name: element.leavePolicy,
          maxWeeks: element.maxWeeks ? element.maxWeeks : 0,
          availableWeeks: element.availableWeeks ? element.availableWeeks : 0,
          usedWeeks: element.usedWeeks ? element.usedWeeks : 0,
          pendingWeeks: element.pendingWeeks ? element.pendingWeeks : 0,
          calculationMethod: this.duration,
        };
        this.policyCalcs.push(week);
      } else if (this.duration === 'hours') {
        const hour = {
          name: element.leavePolicy,
          maxWeeks: element.maxHours ? element.maxHours : 0,
          availableWeeks: element.availableHours ? element.availableHours : 0,
          usedWeeks: element.usedHours ? element.usedHours : 0,
          pendingWeeks: element.pendingHours ? element.pendingHours : 0,
          calculationMethod: this.duration,
        };
        this.policyCalcs.push(hour);
      } else if (this.duration === 'days') {
        const day = {
          name: element.leavePolicy,
          maxWeeks: element.maxDays ? element.maxDays : 0,
          availableWeeks: element.availableDays ? element.availableDays : 0,
          usedWeeks: element.usedDays ? element.usedDays : 0,
          pendingWeeks: element.pendingDays ? element.pendingDays : 0,
          calculationMethod: this.duration,
        };
        this.policyCalcs.push(day);
      }

      if (element.unitNotMatched) {
        this.mismatchedPolicies.push(element);
      }
    });
    let i = 0;
    this.chartItems = [];
    for (const policyCalc of this.policyCalcs) {
      const chartItem = new ChartItem();
      chartItem.id = `chart-${i++}`;
      chartItem.policyCalc = policyCalc;
      this.chartItems.push(chartItem);
    }
    this.loadBalance();
  }

  setPanelStatus() {
    if (this.claims != undefined) {
      if (this.hasOpenLeaveClaims) {
        this.panelExpanded = true;
        this.hasOpenLeaveClaims = true;
      } else {
        this.panelExpanded = false;
        this.hasOpenLeaveClaims = false;
      }
    }
  }
}

class ChartItem {
  id: string;
  chart: any;
  policyCalc: PolicyCalc;
}
